import React from "react";
import { StarRating, scoreBand } from "@/components/site/Stars";
import ScoreLegend from "@/components/site/ScoreLegend";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Info } from "lucide-react";

// Spec v1.1 §1A — FINAL rating rows in Bahasa Indonesia. Labels & questions
// mirror the ReviewWizard 1:1. Kebahagiaan (§1B) and Rekomendasi (§1C) render
// as separate widgets below the 6 bars — NOT part of category_averages.
export const CATEGORY_ROWS = [
  { key: "fasilitas",  label: "Sarana & Fasilitas",             helper: "Apakah gedung, ruang kelas, laboratorium, lapangan, perpustakaan, dan fasilitas lainnya terawat serta mendukung kegiatan belajar?" },
  { key: "keamanan",   label: "Keamanan & Kesejahteraan Siswa", helper: "Seberapa baik sekolah menyediakan lingkungan yang aman bagi siswa, termasuk dalam menangani keamanan, perundungan, dan kesejahteraan siswa?" },
  { key: "kurikulum",  label: "Kurikulum & Pembelajaran",       helper: "Apakah kurikulum dan proses pembelajaran memberikan materi serta tingkat tantangan yang sesuai bagi siswa?" },
  { key: "value",      label: "Kesesuaian Value / Biaya",       helper: "Seberapa sepadan biaya sekolah dengan kualitas pendidikan, layanan, dan fasilitas yang diterima?" },
  { key: "minat",      label: "Pengembangan Minat & Potensi",   helper: "Seberapa baik sekolah menyediakan kesempatan dan dukungan bagi siswa untuk mengembangkan minat dan potensi di luar akademik?" },
  { key: "komunikasi", label: "Komunikasi Sekolah–Orang Tua",   helper: "Seberapa baik sekolah memberikan informasi mengenai perkembangan anak serta berkomunikasi secara terbuka dengan orang tua?" },
];

// Spec §1B — map an average happiness score (1..5) back to the emoji + label
// pair used both in the composer and here in the aggregate panel.
const HAPPINESS_FACES = [
  { min: 4.5, emoji: "😄", label: "Sangat senang" },
  { min: 3.5, emoji: "🙂", label: "Senang" },
  { min: 2.5, emoji: "😐", label: "Biasa" },
  { min: 1.5, emoji: "😕", label: "Kurang" },
  { min: 0,   emoji: "😞", label: "Tidak nyaman" },
];
function happinessFace(avg) {
  const v = Number(avg) || 0;
  return HAPPINESS_FACES.find(f => v >= f.min) || HAPPINESS_FACES[HAPPINESS_FACES.length - 1];
}

/** Convert NPS index (-100..+100) roughly back to a 0-10 scale for ruler display.
 * Fallback for schools that don't yet have `nps_avg_raw` in Mongo. */
export function npsIndexToScale(nps) {
  const v = Number(nps) || 0;
  return Math.max(0, Math.min(10, (v + 100) / 20));
}

/**
 * Premium 3-section aside card:
 *   (A) Ringkasan  — big stars + composite value + colored band pill
 *   (B) Rincian    — per-category StarRating + numeric
 *   (C) NPS Ruler  — "Seberapa Layak Direkomendasikan"
 */
export default function OverallScoreCard({ school, hideOverall = false }) {
  const avg = school?.avg_rating || 0;
  const band = scoreBand(avg);
  const hasNps = (school?.nps_avg_raw !== undefined && school.nps_avg_raw !== 0)
              || (school?.nps_score !== undefined && school.nps_score !== 0);
  return (
    <div className="bg-white border border-[#EAE7E0] rounded-2xl p-6 shadow-[0_4px_18px_-4px_rgba(31,41,55,0.08)]" data-testid="overall-score-card">
      {/* Section A — Ringkasan (hidden when the score-summary-band already shows this) */}
      {!hideOverall && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563]">Overall Score</div>
            <ScoreLegend />
          </div>
          <div className="mt-2 flex items-center justify-between gap-3">
            <StarRating value={avg} size={26} showHalf useBandColor testid="overall-score-stars" />
            <div className="font-serif-display text-4xl leading-none tabular-nums" data-testid="overall-score-value" style={{ color: band.text }}>
              {avg.toFixed(1)}
              <span className="text-lg text-[#4B5563] font-normal"> / 5.0</span>
            </div>
          </div>
          <div className="mt-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider border font-semibold"
               data-testid="overall-score-band"
               style={{ backgroundColor: band.bg, borderColor: band.border, color: band.text }}>
            {band.label}
            <span className="opacity-70 font-normal ml-1">· {school?.review_count || 0} review</span>
          </div>
        </>
      )}

      {/* Section B — Rincian Penilaian (horizontal coral bars) */}
      {school?.category_averages && (
        <div className={(hideOverall ? "" : "mt-5 pt-5 border-t border-[#EAE7E0] ")} data-testid="score-breakdown">
          <div className="text-xs uppercase tracking-widest text-[#D2673E] font-semibold mb-4">Rincian Penilaian</div>
          <TooltipProvider delayDuration={150}>
            <div className="space-y-3">
              {CATEGORY_ROWS.map(({ key, label, helper }) => {
                const cavg = school.category_averages?.[key];
                if (cavg === undefined || cavg === null) return null;
                const pct = Math.max(0, Math.min(1, cavg / 5)) * 100;
                return (
                  <div key={key} className="grid grid-cols-[minmax(0,1fr)_70px_24px] items-center gap-2" data-testid={`breakdown-row-${key}`}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span data-testid={`breakdown-label-${key}`}
                              className="inline-flex items-center gap-1 text-[11px] text-[#1F2937] min-w-0 leading-tight cursor-help hover:text-[#1E3A8A] transition-colors">
                          <span className="min-w-0">{label}</span>
                          <Info size={9} className="opacity-40 flex-shrink-0" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs text-[11px] leading-snug" data-testid={`breakdown-tooltip-${key}`}>
                        {helper}
                      </TooltipContent>
                    </Tooltip>
                    <div className="relative h-1.5 rounded-full bg-[#EAE7E0] overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500"
                        style={{ width: `${pct}%`, backgroundColor: "#D2673E" }}
                        data-testid={`breakdown-bar-${key}`}
                      />
                    </div>
                    <span className="text-xs font-semibold text-[#1F2937] tabular-nums text-right">{cavg.toFixed(1)}</span>
                  </div>
                );
              })}
            </div>
          </TooltipProvider>
        </div>
      )}

      {/* Section B.5 — Kebahagiaan Siswa (emoji, spec §1B — TIDAK masuk skor) */}
      {(() => {
        const hap = Number(school?.happiness_avg) || 0;
        if (hap <= 0) return null;
        const face = happinessFace(hap);
        return (
          <div className="mt-5 pt-5 border-t border-[#EAE7E0]" data-testid="happiness-strip">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-[#8A5A00] font-semibold">Kebahagiaan Siswa</div>
              <span className="text-[10px] uppercase tracking-widest text-[#4B5563] italic">Tidak masuk skor</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-[#FFF8E6] border border-[#F1C97A] px-3 py-2">
              <span className="text-3xl leading-none" aria-hidden="true" data-testid="happiness-emoji">{face.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#8A5A00]" data-testid="happiness-label">{face.label}</div>
                <div className="text-[11px] text-[#4B5563]">Rata-rata dari {school?.review_count || 0} review</div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Section C — Seberapa Layak Direkomendasikan (spec §1C — TIDAK masuk skor) */}
      {hasNps && (
        <div className="mt-5 pt-5 border-t border-[#EAE7E0]" data-testid="nps-score">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-[#D2673E] font-semibold">Seberapa Layak Direkomendasikan</div>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-serif-display font-semibold tabular-nums"
                  style={{ backgroundColor: "#DCFCE7", color: "#166534", border: "1px solid #86EFAC" }}
                  data-testid="nps-value-pill">
              {((school.nps_avg_raw ?? npsIndexToScale(school.nps_score)) || 0).toFixed(1)}
            </span>
          </div>
          {/* Horizontal green bar (mockup-style) */}
          {(() => {
            const raw = school.nps_avg_raw ?? npsIndexToScale(school.nps_score) ?? 0;
            const pct = Math.max(0, Math.min(10, raw)) * 10;
            return (
              <div className="relative h-1.5 rounded-full bg-[#EAE7E0] overflow-hidden">
                <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${pct}%`, backgroundColor: "#166534" }} data-testid="nps-bar-fill" />
              </div>
            );
          })()}
          <div className="mt-1.5 flex items-center justify-between text-[10px] text-[#4B5563]">
            <span>0</span>
            <span className="italic">Rata-rata dari semua review</span>
            <span>10</span>
          </div>
        </div>
      )}
    </div>
  );
}
